import React from "react";
import { Check, ShieldCheck, Sparkles } from "lucide-react";

const PLAN_1 = [
  "Custom AI agent built for your business",
  "Trained on your FAQs, services & objections",
  "Calendly integration for live booking",
  "Lead capture & contact collection",
  "Objection handling configuration",
  "Full deployment on your website",
  "1-year agent warranty & support",
];

const PLAN_2 = [
  "Custom website designed for your brand",
  "Mobile-optimized & fast-loading",
  "SEO foundations built in",
  "Booking & inquiry integrations",
  "Launch support & handoff",
  "AI agent fully deployed on launch day",
  "1-year warranty on agent & site",
];

export default function Pricing({ onBookDemo }) {
  return (
    <section id="pricing" data-testid="pricing-section" className="bg-[#0A0F1E] fs-section">
      <div className="fs-container">
        <div className="text-center max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#C9A84C]" />
            <span className="fs-tag">Pricing</span>
            <span className="h-px w-8 bg-[#C9A84C]" />
          </div>
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-[0.03em] leading-[0.95] text-white">
            Simple Pricing. <span className="text-[#C9A84C]">No Surprises.</span>
          </h2>
          <p className="mt-6 text-lg text-white/65 leading-relaxed">
            One flat investment. Your agent is live in 7 days and comes with a 1-year warranty.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Featured */}
          <article
            data-testid="pricing-card-featured"
            className="relative bg-[#131929] border-2 border-[#C9A84C] rounded-2xl p-8 md:p-10 shadow-[0_30px_80px_-40px_rgba(201,168,76,0.6)]"
          >
            <span className="absolute -top-3 left-8 bg-[#C9A84C] text-[#0A0F1E] text-[0.65rem] font-bold uppercase tracking-[0.25em] px-3 py-1 rounded-sm">
              Most Popular
            </span>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#C9A84C] font-semibold mb-2">
                  Most Popular
                </div>
                <h3 className="font-display text-3xl md:text-4xl uppercase tracking-[0.03em] text-white">
                  AI Agent Installation
                </h3>
              </div>
              <Sparkles className="text-[#C9A84C]" size={28} strokeWidth={1.4} />
            </div>

            <div className="mt-8 flex items-baseline gap-3">
              <div className="font-display text-6xl md:text-7xl text-white tracking-[0.03em]">$1,997</div>
            </div>
            <p className="mt-2 text-sm text-white/55">
              One-time investment · You already have a website
            </p>

            <p className="mt-6 text-white/75 leading-relaxed">
              We plug your AI sales agent directly into your existing website.
              Done in 7 days or less.
            </p>

            <div className="mt-8 border-t border-white/10 pt-6">
              <div className="text-[0.7rem] uppercase tracking-[0.22em] text-white/55 mb-4">
                What's included
              </div>
              <ul className="space-y-3">
                {PLAN_1.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white">
                    <Check className="text-[#C9A84C] mt-0.5 shrink-0" size={17} strokeWidth={2.2} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              data-testid="pricing-cta-featured"
              onClick={onBookDemo}
              className="btn-primary w-full mt-8"
            >
              Get Started — $1,997
            </button>
            <p className="mt-4 flex items-center justify-center gap-2 text-xs text-white/60">
              <ShieldCheck size={14} className="text-[#C9A84C]" />
              1-year warranty included · Any issues, we fix it
            </p>
          </article>

          {/* Plain */}
          <article
            data-testid="pricing-card-website"
            className="relative bg-[#131929] border border-white/15 rounded-2xl p-8 md:p-10 transition-all duration-300 hover:border-[#C9A84C]/55"
          >
            <div className="text-[0.7rem] uppercase tracking-[0.22em] text-white/60 font-semibold mb-2">
              No Website Yet?
            </div>
            <h3 className="font-display text-3xl md:text-4xl uppercase tracking-[0.03em] text-white">
              Website + AI Agent
            </h3>

            <div className="mt-8 flex items-baseline gap-3">
              <div className="font-display text-6xl md:text-7xl text-white tracking-[0.03em]">$3,997</div>
            </div>
            <p className="mt-2 text-sm text-white/55">
              One-time investment · We build everything from scratch
            </p>

            <p className="mt-6 text-white/75 leading-relaxed">
              No website? In 2026, your business is invisible without one. We
              build it and launch your agent on it.
            </p>

            <div className="mt-8 border-t border-white/10 pt-6">
              <div className="text-[0.7rem] uppercase tracking-[0.22em] text-white/55 mb-4">
                Everything in Installation, plus
              </div>
              <ul className="space-y-3">
                {PLAN_2.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white">
                    <Check className="text-[#C9A84C] mt-0.5 shrink-0" size={17} strokeWidth={2.2} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              data-testid="pricing-cta-website"
              onClick={onBookDemo}
              className="btn-secondary w-full mt-8"
            >
              Get Started — $3,997
            </button>
            <p className="mt-4 flex items-center justify-center gap-2 text-xs text-white/60">
              <ShieldCheck size={14} className="text-[#C9A84C]" />
              1-year warranty included · Any issues, we fix it
            </p>
          </article>
        </div>

        {/* ROI note */}
        <div
          data-testid="roi-note"
          className="mt-12 max-w-3xl mx-auto rounded-xl border border-[#C9A84C]/30 bg-[#131929] px-6 py-5 flex items-start gap-4"
        >
          <div className="h-10 w-10 rounded-md bg-[#C9A84C] text-[#0A0F1E] flex items-center justify-center shrink-0">
            <Sparkles size={18} strokeWidth={1.8} />
          </div>
          <p className="text-sm md:text-[0.95rem] text-white/80 leading-relaxed">
            Most clients recover this investment within their first 1–2 booked
            appointments. One event booking. One legal case. One real estate
            deal. One high-ticket client. That's all it takes.
          </p>
        </div>
      </div>
    </section>
  );
}
